/**
 * Cookie Management Utility for MediTimeX Application
 * Handles all cookie operations including get, set, delete, and JSON serialization
 */

// Default cookie options
const DEFAULT_OPTIONS = {
  expires: 365, // days
  path: '/',
  secure: window.location.protocol === 'https:',
  sameSite: 'lax'
};

/**
 * Set a cookie with the given name, value, and options
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {Object} options - Cookie options (expires, path, secure, sameSite)
 */
export const setCookie = (name, value, options = {}) => {
  try {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (opts.expires) {
      const date = new Date();
      date.setTime(date.getTime() + (opts.expires * 24 * 60 * 60 * 1000));
      cookieString += `; expires=${date.toUTCString()}`;
    }

    if (opts.path) {
      cookieString += `; path=${opts.path}`;
    }

    if (opts.secure) {
      cookieString += `; secure`;
    }

    if (opts.sameSite) {
      cookieString += `; samesite=${opts.sameSite}`;
    }

    document.cookie = cookieString;
    return true;
  } catch (error) {
    console.error('Error setting cookie:', error);
    return false;
  }
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
  try {
    const nameEQ = encodeURIComponent(name) + '=';
    const cookies = document.cookie.split(';');
    
    for (let cookie of cookies) {
      let c = cookie.trim();
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting cookie:', error);
    return null;
  }
};

/**
 * Delete a cookie by name
 * @param {string} name - Cookie name
 * @param {string} path - Cookie path (default: '/')
 */
export const deleteCookie = (name, path = '/') => {
  try {
    document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
    return true;
  } catch (error) {
    console.error('Error deleting cookie:', error);
    return false;
  }
};

/**
 * Set a JSON object as a cookie
 * @param {string} name - Cookie name
 * @param {Object} value - JSON object to store
 * @param {Object} options - Cookie options
 */
export const setJsonCookie = (name, value, options = {}) => {
  try {
    const jsonString = JSON.stringify(value);
    return setCookie(name, jsonString, options);
  } catch (error) {
    console.error('Error setting JSON cookie:', error);
    return false;
  }
};

/**
 * Get a JSON object from a cookie
 * @param {string} name - Cookie name
 * @param {Object} defaultValue - Default value if cookie doesn't exist or is invalid
 * @returns {Object} Parsed JSON object or default value
 */
export const getJsonCookie = (name, defaultValue = {}) => {
  try {
    const cookieValue = getCookie(name);
    if (!cookieValue) {
      return defaultValue;
    }
    return JSON.parse(cookieValue);
  } catch (error) {
    console.error('Error parsing JSON cookie:', error);
    return defaultValue;
  }
};

/**
 * Check if a cookie exists
 * @param {string} name - Cookie name
 * @returns {boolean} True if cookie exists, false otherwise
 */
export const cookieExists = (name) => {
  return getCookie(name) !== null;
};

/**
 * Get all cookies as an object
 * @returns {Object} Object with all cookies
 */
export const getAllCookies = () => {
  try {
    const cookies = {};
    const cookieArray = document.cookie.split(';');
    
    for (let cookie of cookieArray) {
      const [name, value] = cookie.trim().split('=');
      if (name && value) {
        cookies[decodeURIComponent(name)] = decodeURIComponent(value);
      }
    }
    return cookies;
  } catch (error) {
    console.error('Error getting all cookies:', error);
    return {};
  }
};

// Application-specific cookie names
export const COOKIE_NAMES = {
  WEEK_PLAN_SETTINGS: 'meditimex_weekplan_settings',
  USER_PREFERENCES: 'meditimex_user_preferences',
  THEME_SETTINGS: 'meditimex_theme_settings'
};

// Week Plan Settings specific functions
export const getWeekPlanSettings = () => {
  return getJsonCookie(COOKIE_NAMES.WEEK_PLAN_SETTINGS, {
    showSubOrganisation: true,
    showComments: true,
    showWorkDuration: true,
    autoSave: false
  });
};

export const setWeekPlanSettings = (settings) => {
  const currentSettings = getWeekPlanSettings();
  const updatedSettings = { ...currentSettings, ...settings };
  return setJsonCookie(COOKIE_NAMES.WEEK_PLAN_SETTINGS, updatedSettings);
};

export const resetWeekPlanSettings = () => {
  return deleteCookie(COOKIE_NAMES.WEEK_PLAN_SETTINGS);
};
